import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useState,
	useRef,
} from "react";
import { createPortal } from "react-dom";
import { TextField } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const CustomAutocomplete = ({
	options = [],
	value = null,
	onChange,
	placeholder = "",
	getOptionLabel = (option) => option?.title || "",
	getOptionId = (option) => option?.id,
	noOptionsText = "Nenhuma opção encontrada",
	disabled = false,
	label = "",
	required = false,
	size = "medium",
	maxHeight = "300px",
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isTyping, setIsTyping] = useState(false);
	const [filteredOptions, setFilteredOptions] = useState([]);
	const [dropdownPosition, setDropdownPosition] = useState(null);
	const containerRef = useRef(null);

	useEffect(() => {
		if (!options || options.length === 0) {
			setFilteredOptions([]);
			return;
		}

		if (!searchTerm || searchTerm.trim() === "") {
			const sorted = [...options].sort((a, b) =>
				getOptionLabel(a).localeCompare(getOptionLabel(b), "pt-BR")
			);
			setFilteredOptions(sorted);
		} else {
			const termo = searchTerm.toLowerCase().trim();
			const filtered = options.filter((option) =>
				getOptionLabel(option).toLowerCase().includes(termo)
			);
			filtered.sort((a, b) =>
				getOptionLabel(a).localeCompare(getOptionLabel(b), "pt-BR")
			);
			setFilteredOptions(filtered);
		}
	}, [searchTerm, options, getOptionLabel]);

	const handleInputChange = (e) => {
		if (value && !isTyping) {
			onChange && onChange(null);
		}
		setIsTyping(true);
		setSearchTerm(e.target.value);
		setIsDropdownOpen(true);

		if (!e.target.value) {
			onChange && onChange(null);
		}
	};

	const handleKeyDown = (e) => {
		if (
			value &&
			!isTyping &&
			(e.key === "Backspace" ||
				e.key === "Delete" ||
				(e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey))
		) {
			setIsTyping(true);
			const currentText = getOptionLabel(value);

			if (e.key === "Backspace") {
				setSearchTerm(currentText.slice(0, -1));
			} else if (e.key === "Delete") {
				setSearchTerm(currentText);
			} else if (e.key.length === 1) {
				setSearchTerm(currentText + e.key);
				e.preventDefault();
			}

			onChange && onChange(null);
			setIsDropdownOpen(true);
		}
	};

	const handleOptionClick = (option) => {
		setIsTyping(false);
		setSearchTerm("");
		setIsDropdownOpen(false);
		onChange && onChange(option);
	};

	useEffect(() => {
		if (value && searchTerm && !isTyping) {
			setSearchTerm("");
		}
	}, [value, searchTerm, isTyping]);

	useEffect(() => {
		if (value) {
			setIsTyping(false);
		}
	}, [value]);

	const handleInputClick = () => {
		if (value && !isTyping) {
			setSearchTerm(getOptionLabel(value));
			setIsTyping(true);
			onChange && onChange(null);
		}
		setIsDropdownOpen(true);
	};

	const handleFocus = () => {
		setIsDropdownOpen(true);
	};

	const handleBlur = () => {
		setTimeout(() => {
			setIsDropdownOpen(false);
			setIsTyping(false);
			if (!value && searchTerm) {
				setSearchTerm("");
			}
		}, 150);
	};

	const handleClear = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setSearchTerm("");
		setIsTyping(false);
		onChange && onChange(null);
	};

	const displayValue = isTyping
		? searchTerm
		: value
		? getOptionLabel(value)
		: searchTerm;

	// Calcula a posição da lista em coordenadas de viewport (position: fixed).
	// Assim a lista nunca é cortada pelo modal/container com overflow e abre
	// para cima quando não há espaço suficiente abaixo do campo.
	const updateDropdownPosition = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		const rect = container.getBoundingClientRect();
		const gap = 4;
		const margin = 8;
		const minHeight = 120;
		const configuredMaxHeight = parseInt(maxHeight, 10) || 300;

		const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
		const spaceAbove = rect.top - gap - margin;
		const openUpwards =
			spaceBelow < Math.min(configuredMaxHeight, minHeight) &&
			spaceAbove > spaceBelow;
		const availableSpace = openUpwards ? spaceAbove : spaceBelow;

		setDropdownPosition({
			left: rect.left,
			width: rect.width,
			maxHeight: Math.max(
				minHeight,
				Math.min(configuredMaxHeight, availableSpace)
			),
			top: openUpwards ? undefined : rect.bottom + gap,
			bottom: openUpwards ? window.innerHeight - rect.top + gap : undefined,
		});
	}, [maxHeight]);

	useLayoutEffect(() => {
		if (!isDropdownOpen) {
			setDropdownPosition(null);
			return;
		}

		updateDropdownPosition();

		const handleReposition = () => updateDropdownPosition();
		// capture: true para acompanhar scroll de containers internos (modais)
		window.addEventListener("scroll", handleReposition, true);
		window.addEventListener("resize", handleReposition);

		return () => {
			window.removeEventListener("scroll", handleReposition, true);
			window.removeEventListener("resize", handleReposition);
		};
	}, [isDropdownOpen, filteredOptions.length, updateDropdownPosition]);

	const dropdownBaseStyle = dropdownPosition
		? {
				position: "fixed",
				left: `${dropdownPosition.left}px`,
				width: `${dropdownPosition.width}px`,
				...(dropdownPosition.top !== undefined
					? { top: `${dropdownPosition.top}px` }
					: { bottom: `${dropdownPosition.bottom}px` }),
				backgroundColor: "white",
				borderRadius: "14px",
				zIndex: 1500,
				boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
				fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
		  }
		: null;

	return (
		<div ref={containerRef} style={{ position: "relative", width: "100%" }}>
			<TextField
				fullWidth
				label={label}
				required={required}
				placeholder={placeholder}
				value={displayValue}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onClick={handleInputClick}
				onFocus={handleFocus}
				onBlur={handleBlur}
				disabled={disabled}
				size={size}
				autoComplete="off"
				inputProps={{
					style: { paddingRight: value && !disabled ? "56px" : "32px" },
				}}
			/>

			{/* Icons positioned outside TextField to avoid notch interference */}
			<div
				style={{
					position: "absolute",
					right: "12px",
					top: "50%",
					transform: "translateY(-50%)",
					display: "flex",
					alignItems: "center",
					gap: "2px",
					pointerEvents: value && !disabled ? "auto" : "none",
				}}
			>
				{value && !disabled && (
					<ClearIcon
						onMouseDown={handleClear}
						sx={{
							fontSize: 20,
							color: "rgba(0, 0, 0, 0.54)",
							cursor: "pointer",
							"&:hover": { color: "rgba(0, 0, 0, 0.87)" },
						}}
					/>
				)}
				<ArrowDropDownIcon
					sx={{
						fontSize: 24,
						color: "rgba(0, 0, 0, 0.54)",
						pointerEvents: "none",
					}}
				/>
			</div>

			{/* Dropdown */}
			{isDropdownOpen &&
				filteredOptions.length > 0 &&
				dropdownBaseStyle &&
				createPortal(
				<div
					style={{
						...dropdownBaseStyle,
						maxHeight: `${dropdownPosition.maxHeight}px`,
						overflowY: "auto",
					}}
				>
					{filteredOptions.map((option) => (
						<div
							key={getOptionId(option)}
							style={{
								padding: "6px 16px",
								cursor: "pointer",
								fontSize: "0.875rem",
								fontWeight: "400",
								lineHeight: "1.5",
								color: "rgba(0, 0, 0, 0.87)",
								backgroundColor: "transparent",
								transition:
									"background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
								minHeight: "42px",
								display: "flex",
								alignItems: "center",
								width: "100%",
								boxSizing: "border-box",
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.backgroundColor =
									"rgba(0, 0, 0, 0.04)";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.backgroundColor = "transparent";
							}}
							onMouseDown={(e) => {
								e.preventDefault();
							}}
							onClick={(e) => {
								e.stopPropagation();
								handleOptionClick(option);
							}}
						>
							{getOptionLabel(option)}
						</div>
					))}
				</div>,
					document.body
				)}

			{/* No options message */}
			{isDropdownOpen &&
				filteredOptions.length === 0 &&
				searchTerm &&
				dropdownBaseStyle &&
				createPortal(
				<div
					style={{
						...dropdownBaseStyle,
						padding: "6px 16px",
						textAlign: "center",
						color: "rgba(0, 0, 0, 0.6)",
						fontSize: "0.875rem",
						minHeight: "42px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{noOptionsText}
				</div>,
					document.body
				)}
		</div>
	);
};

export default CustomAutocomplete;
